import { Dictionary } from '@reduxjs/toolkit';
import { filter, map } from 'lodash';
import React from 'react';
import { Link } from 'react-router-dom';
import { DynamicImage } from 'src/shared/design-system/DynamicImage';
import { Routes } from 'src/shared/Routes';
import { getIdSluggedPath } from 'src/shared/utils/RouteUtilities';
import { useSelector } from 'src/store';
import { selectShefDeliveryDateCapacityInfo } from 'src/store/cart/selectors';
import { CapacityInfo, LoadedCartShef, WholeCartLineItem } from 'src/store/cart/types';
import { getAdjustedFoodItemAvailability } from 'src/store/cart/utils/capacity';
import styled from 'styled-components';
import { Colors, Spacing } from 'web-common/src/shared/styles';
import { CartItemSoldOutError } from './cart/CartItemSoldOutError';
import { ConsumerOrderCartCapacityWarning } from './ConsumerOrderCartCapacityWarning';
import { UpdatableItemType } from './ConsumerOrderCartInfo';
import { MultiCartLineItem } from './MultiCart/MultiCartLineItem';
import { ProfilePhoto, ShefInfoContainer, Title } from './MultiCart/ShefOrderBlockHeaderStyles';

const LineItemsContainer = styled.div`
  > * {
    padding-top: ${Spacing.SINGLE};
    padding-bottom: ${Spacing.SINGLE};
    border-top: 1px solid ${Colors.GRAY_LITE_20};

    &:first-child {
      border-top: none;
    }
  }
`;

interface IConsumerOrderCartLineItemsProps {
  capacityInfo: CapacityInfo;
  showDishImages: boolean;
  shef: LoadedCartShef;
  increment: (item: UpdatableItemType) => void;
  decrement: (item: UpdatableItemType) => void;
  lineItems: WholeCartLineItem[];
  isEditable: boolean;
  unavailableLineItemIds: Set<number>;
  deliveryDate: string;
  lineItemQuantitiesInEditingOrder: Dictionary<number>;
  showUnavailableItems: boolean;
  isMultiCart: boolean;
  isEditingOrder?: boolean;
}

export const ConsumerOrderCartLineItems: React.FC<IConsumerOrderCartLineItemsProps> = ({
  capacityInfo,
  lineItemQuantitiesInEditingOrder,
  unavailableLineItemIds,
  lineItems,
  deliveryDate,
  shef,
  showUnavailableItems,
  isEditingOrder = false,
}) => {
  const shefMenuPath = getIdSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, shef.id);

  const unavailableItems = filter(lineItems, (item) => unavailableLineItemIds.has(item.id));
  const { numDishesRemaining, numDishesAvailable } = useSelector((state) =>
    selectShefDeliveryDateCapacityInfo(state.cart, shef.id, isEditingOrder, deliveryDate)
  );

  return (
    <div className='flex flex-col'>
      {!!shefMenuPath && (
        <div className='flex flex-col rounded-lg bg-neutral-50'>
          <Link to={shefMenuPath} className='self-start p-2'>
            <ShefInfoContainer>
              <ProfilePhoto>
                <DynamicImage
                  className='result-image shef-result-image'
                  sourceHeight={32}
                  sourceWidth={32}
                  height={32}
                  width={32}
                  src={shef.imageUrl}
                  alt={`${shef.name} image`}
                />
              </ProfilePhoto>
              <Title>
                <u>Shef {shef.name}</u>
              </Title>
            </ShefInfoContainer>
          </Link>
        </div>
      )}

      <ConsumerOrderCartCapacityWarning
        isOrderingAllRemainingItems={capacityInfo.isOrderingAllRemainingItems}
        isOrderingMoreThanDeliveryDateCapacity={capacityInfo.isOrderingMoreThanDeliveryDateCapacity}
        numDishesRemaining={capacityInfo.numDishesRemaining}
      />

      {unavailableItems.length > 0 && (
        <CartItemSoldOutError itemDisplayTexts={map(unavailableItems, (item) => item.foodItem.name ?? '')} />
      )}

      <LineItemsContainer>
        {lineItems.map((item) => {
          const quantityInEditingOrder = lineItemQuantitiesInEditingOrder[item.foodItem.id] ?? 0;
          const capacity = getAdjustedFoodItemAvailability({
            foodItemsAvailabilities: item.foodItem.availability,
            deliveryDate,
            quantityInEditingOrder,
          });
          const unavailable = unavailableLineItemIds.has(item.id);

          if (!showUnavailableItems && unavailable) return null;

          return (
            <MultiCartLineItem
              key={item.id}
              deliveryDate={deliveryDate}
              lineItem={item}
              shefId={shef.id}
              unavailable={unavailable}
              numAvailable={capacity ?? Infinity}
              shefMenuUrl={shefMenuPath}
              shefNumDishesAvailable={numDishesAvailable}
              shefNumDishesRemaining={numDishesRemaining}
              isEditingOrder={isEditingOrder}
              paddingX={Spacing.HALF}
              paddingY={Spacing.HALF}
            />
          );
        })}
      </LineItemsContainer>
    </div>
  );
};
